import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { DOMSerializer } from '@tiptap/pm/model';
import { useMutation } from '@tanstack/react-query';
import {
    PiArticleBold,
    PiChalkboardTeacherBold,
    PiCheckSquareOffsetBold,
    PiListNumbersBold,
    PiMagicWandBold,
    PiPushPinBold,
} from 'react-icons/pi';
import { Button, Switch } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { AiBadge, AiDisclaimer, AiError, AiThinking, aiErrorMessage } from '@v2/features/ai/aiShared';
import DocContent from './DocContent';

type Action = 'draft' | 'improve' | 'didactic' | 'steps' | 'checklist' | 'summary';

const ACTIONS: { key: Action; label: string; hint: string; icon: typeof PiArticleBold }[] = [
    { key: 'draft', label: 'Escrever rascunho', hint: 'A partir das suas anotações', icon: PiArticleBold },
    { key: 'didactic', label: 'Deixar didático', hint: 'Para quem está chegando', icon: PiChalkboardTeacherBold },
    { key: 'steps', label: 'Virar passo a passo', hint: 'Lista numerada na ordem', icon: PiListNumbersBold },
    { key: 'improve', label: 'Melhorar a escrita', hint: 'Mais claro, mesmos fatos', icon: PiMagicWandBold },
    { key: 'checklist', label: 'Gerar checklist', hint: 'Para conferir na prática', icon: PiCheckSquareOffsetBold },
    { key: 'summary', label: 'Resumo rápido', hint: 'Caixa no topo do texto', icon: PiPushPinBold },
];

function selectionHtml(editor: Editor): string {
    const { from, to, empty } = editor.state.selection;
    if (empty) return '';
    const fragment = DOMSerializer.fromSchema(editor.schema).serializeFragment(editor.state.doc.slice(from, to).content);
    const div = document.createElement('div');
    div.appendChild(fragment);
    return div.innerHTML;
}

export default function DocAiModal({
    open,
    onClose,
    editor,
    title,
    documentId,
}: {
    open: boolean;
    onClose: () => void;
    editor: Editor;
    title: string;
    documentId?: number;
}) {
    const [action, setAction] = useState<Action>('didactic');
    const [notes, setNotes] = useState('');
    const [useHub, setUseHub] = useState(true);
    const [selected, setSelected] = useState('');

    const docEmpty = editor.isEmpty;

    useEffect(() => {
        if (!open) return;
        const sel = selectionHtml(editor);
        setSelected(sel);
        setAction(editor.isEmpty ? 'draft' : 'didactic');
        gen.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const scopeHtml = action === 'draft' ? editor.getHTML() : selected || editor.getHTML();

    const gen = useMutation({
        mutationFn: async () =>
            (
                await api.post<{ data: { html: string } }>('/ai/documents/assist', {
                    action,
                    title,
                    html: scopeHtml,
                    notes,
                    use_hub_context: useHub,
                    document_id: documentId,
                })
            ).data.data.html,
    });

    const apply = (mode: 'replace-selection' | 'insert' | 'replace-all') => {
        const html = gen.data ?? '';
        if (mode === 'replace-all') editor.chain().focus().setContent(html, { emitUpdate: true }).run();
        else if (mode === 'replace-selection') editor.chain().focus().insertContent(html).run();
        else editor.chain().focus().insertContentAt(action === 'summary' ? 0 : editor.state.selection.to, html).run();
        onClose();
    };

    const needsNotes = action === 'draft';
    const canRun = needsNotes ? notes.trim().length > 0 || title.trim().length > 0 : !docEmpty;

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title="Escrever com IA"
            description={selected && action !== 'draft' ? 'Vai trabalhar no trecho selecionado.' : 'Vai trabalhar no documento inteiro.'}
            size="2xl"
            footer={
                gen.isSuccess ? (
                    <>
                        <Button variant="outline" onClick={() => gen.reset()}>
                            Voltar
                        </Button>
                        {selected && action !== 'draft' && action !== 'summary' && action !== 'checklist' ? (
                            <Button onClick={() => apply('replace-selection')}>Substituir o trecho</Button>
                        ) : action === 'draft' || action === 'improve' || action === 'didactic' || action === 'steps' ? (
                            <>
                                <Button variant="outline" onClick={() => apply('insert')}>
                                    Inserir no cursor
                                </Button>
                                <Button onClick={() => apply('replace-all')}>Substituir o documento</Button>
                            </>
                        ) : (
                            <Button onClick={() => apply('insert')}>{action === 'summary' ? 'Inserir no topo' : 'Inserir no cursor'}</Button>
                        )}
                    </>
                ) : (
                    <>
                        <Button variant="outline" onClick={onClose}>
                            Fechar
                        </Button>
                        <Button disabled={!canRun || gen.isPending} isLoading={gen.isPending} onClick={() => gen.mutate()}>
                            Gerar
                        </Button>
                    </>
                )
            }
        >
            {gen.isPending ? (
                <AiThinking label="Escrevendo…" />
            ) : gen.isSuccess ? (
                <div className="space-y-3">
                    <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-rose-200 bg-rose-50/30 p-4 dark:border-rose-500/30 dark:bg-rose-500/5">
                        <DocContent html={gen.data} />
                    </div>
                    <AiDisclaimer>
                        <span>· Trechos com [CONFIRMAR] precisam da sua informação.</span>
                    </AiDisclaimer>
                </div>
            ) : (
                <div className="space-y-4">
                    {gen.isError && <AiError message={aiErrorMessage(gen.error)} />}
                    <div className="grid gap-2 sm:grid-cols-3">
                        {ACTIONS.map((a) => {
                            const Icon = a.icon;
                            const disabled = a.key !== 'draft' && docEmpty;
                            return (
                                <button
                                    key={a.key}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => setAction(a.key)}
                                    className={cn(
                                        'flex items-start gap-2 rounded-xl border p-3 text-left transition disabled:opacity-40',
                                        action === a.key
                                            ? 'border-rose-400 bg-rose-50 ring-1 ring-rose-300 dark:border-rose-500/60 dark:bg-rose-500/10'
                                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-700',
                                    )}
                                >
                                    <Icon className="mt-0.5 size-5 shrink-0 text-rose-600 dark:text-rose-300" />
                                    <span>
                                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{a.label}</span>
                                        <span className="block text-[11px] text-gray-500">{a.hint}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                            {needsNotes ? 'Anotações (tópicos, regras, prazos — do jeito que estiver)' : 'Orientação extra (opcional)'}
                        </span>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={needsNotes ? 7 : 3}
                            placeholder={
                                needsNotes
                                    ? 'Ex.: recebimento no CD — conferir lacre, contar volumes pelo romaneio, foto de avaria, alocar na prateleira, devolver container em até 7 dias…'
                                    : 'Ex.: foque no que o vendedor precisa saber'
                            }
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                        />
                    </label>
                    <div className="flex items-start justify-between gap-3">
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                            Usar os documentos de processo do Hub como referência (mantém os mesmos nomes de telas e regras)
                        </span>
                        <Switch
                            size="sm"
                            checked={useHub}
                            onChange={(e) => setUseHub(e.target.checked)}
                            aria-label="Usar os documentos de processo do Hub como referência"
                            className="shrink-0 [&_label]:my-0"
                        />
                    </div>
                    <p className="flex items-center gap-2 text-[11px] text-gray-400">
                        <AiBadge /> A IA não inventa prazos nem regras: o que faltar vem marcado como [CONFIRMAR].
                    </p>
                </div>
            )}
        </V2Modal>
    );
}
